import type { NavigateFunction } from 'react-router-dom';

// ─── Form Event Map ───────────────────────────────────────────────────────────

export const FORM_EVENT_MAP: Record<string, string> = {
  deal_form: 'cros:open-deal-form',
  brand_form: 'cros:open-brand-form',
  invoice_form: 'cros:open-invoice-form',
  expense_form: 'cros:open-expense-form',
  goal_form: 'cros:open-goal-form',
  deliverable_form: 'cros:open-deliverable-form',
  time_entry_form: 'cros:open-time-entry-form',
  pitch_writer: 'cros:open-pitch-writer',
  escalation_email: 'cros:open-escalation-email',
  contract_upload: 'cros:open-contract-upload',
  outreach_form: 'cros:open-outreach-form',
  media_kit_editor: 'cros:open-media-kit-editor',
  nda_form: 'cros:open-nda-form',
};

export const PAGE_ROUTES: Record<string, string> = {
  home: '/',
  dashboard: '/dashboard',
  deals: '/deals',
  deal_detail: '/deals/',  // + id
  payments: '/payments',
  brands: '/brands',
  brand_detail: '/brands/', // + id
  outreach: '/outreach',
  deal_reviewer: '/deal-reviewer',
  contract_analyzer: '/contract-analyzer',
  media_kit: '/media-kit',
  analytics: '/analytics',
  industry_intel: '/industry-intel',
  finances: '/finances',
  legal: '/legal',
  collabs: '/collabs',
  goals: '/goals',
  settings: '/settings',
};

// ─── Confirmation Data ────────────────────────────────────────────────────────

export interface ConfirmationData {
  action: string;
  tool: string;
  input: Record<string, unknown>;
  endpoint: string;
  method: string;
  body: Record<string, unknown>;
}

// ─── Handle Navigation ────────────────────────────────────────────────────────

export function handleNavigation(
  nav: { page: string; params?: Record<string, string> },
  navigateFn: NavigateFunction
): void {
  const baseRoute = PAGE_ROUTES[nav.page] ?? `/${nav.page.toLowerCase().replace(/_/g, '-')}`;

  if (nav.params && Object.keys(nav.params).length > 0) {
    // If params include an id and the route ends with '/', append it
    const id = nav.params.id;
    if (id && baseRoute.endsWith('/')) {
      const remainingParams = { ...nav.params };
      delete remainingParams.id;
      if (Object.keys(remainingParams).length > 0) {
        const search = new URLSearchParams(remainingParams).toString();
        navigateFn(`${baseRoute}${id}?${search}`);
      } else {
        navigateFn(`${baseRoute}${id}`);
      }
    } else {
      const search = new URLSearchParams(nav.params).toString();
      navigateFn(`${baseRoute}?${search}`);
    }
  } else {
    navigateFn(baseRoute);
  }
}

// ─── Handle Form Open ─────────────────────────────────────────────────────────

export function handleFormOpen(form: { form: string; prefill?: Record<string, unknown> }): void {
  const eventName = FORM_EVENT_MAP[form.form];
  if (!eventName) {
    console.error(`[handleFormOpen] No FORM_EVENT_MAP entry for "${form.form}" — add it to src/lib/ai-tools.ts`);
    return;
  }

  window.dispatchEvent(
    new CustomEvent(eventName, {
      detail: { prefill: form.prefill ?? {} },
    })
  );
}

// ─── Handle Confirmation ─────────────────────────────────────────────────────

export function handleConfirmation(confirmation: ConfirmationData): ConfirmationData {
  // Returns the data as-is; caller is responsible for rendering ConfirmationCard
  return confirmation;
}

// ─── Action → Endpoint mapping ──────────────────────────────────────────────
// The AI server returns write-tool results as `{ action, details }` and
// expects the *frontend* to know which endpoint executes that action. This
// mapping is the single source of truth. To wire up a new write tool:
//   1) add the tool to ai-tools-definitions.ts (server)
//   2) add a `case` to executeWriteTool (server) that returns
//      writeConfirmation('<action>', { ...input, creator_id })
//   3) add a `case` here that turns those details into a ConfirmationData
//
// Returns null for unknown actions — the chat hook logs a warning and skips
// rendering, which is preferable to dropping the user into a broken card.

interface RawConfirmation {
  action: string;
  details: Record<string, unknown>;
}

const stripUndefined = (obj: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
};

export function buildConfirmationData(raw: RawConfirmation): ConfirmationData | null {
  const { action, details } = raw;
  // Most server case statements do `{ ...input, creator_id }`. The endpoints
  // pull creator from auth, so we always strip it before sending.
  const d = { ...details };
  delete d.creator_id;

  switch (action) {
    // ── Contracts (versioning) ──────────────────────────────────────────────
    case 'create_contract_version': {
      const contractId = d.contract_id as string;
      if (!contractId) return null;
      return {
        action: 'Create new contract version',
        tool: action,
        input: details,
        endpoint: `/api/contracts/${contractId}/versions`,
        method: 'POST',
        body: stripUndefined({
          contractText:   d.contract_text,
          contractName:   d.contract_name,
          changesSummary: d.changes_summary,
        }),
      };
    }

    // ── Deals ───────────────────────────────────────────────────────────────
    // The deals POST schema accepts snake_case (with camelCase aliases on a
    // few fields), so we forward details as-is.
    case 'create_deal':
      return {
        action: 'Create deal',
        tool: action,
        input: details,
        endpoint: '/api/deals',
        method: 'POST',
        body: d,
      };
    case 'update_deal': {
      const dealId = d.deal_id as string;
      if (!dealId) return null;
      const { deal_id: _ignored, ...rest } = d;
      void _ignored;
      return {
        action: 'Update deal',
        tool: action,
        input: details,
        endpoint: `/api/deals/${dealId}`,
        method: 'PATCH',
        body: rest,
      };
    }
    case 'update_deal_status': {
      const dealId = d.deal_id as string;
      if (!dealId) return null;
      return {
        action: 'Update deal status',
        tool: action,
        input: details,
        endpoint: `/api/deals/${dealId}/status`,
        method: 'PATCH',
        body: { status: d.new_status },
      };
    }
    case 'delete_deal': {
      const dealId = d.deal_id as string;
      if (!dealId) return null;
      return {
        action: 'Delete deal',
        tool: action,
        input: details,
        endpoint: `/api/deals/${dealId}`,
        method: 'DELETE',
        body: {},
      };
    }
    case 'submit_postmortem': {
      const dealId = d.deal_id as string;
      if (!dealId) return null;
      const { deal_id: _ignored, ...rest } = d;
      void _ignored;
      return {
        action: 'Submit deal post-mortem',
        tool: action,
        input: details,
        endpoint: `/api/deals/${dealId}/postmortem`,
        method: 'POST',
        body: rest,
      };
    }

    // ── Outreach ────────────────────────────────────────────────────────────
    case 'create_outreach':
      return {
        action: 'Create outreach',
        tool: action,
        input: details,
        endpoint: '/api/outreach',
        method: 'POST',
        body: stripUndefined({
          brandName:    d.brand_name,
          contactName:  d.contact_name,
          contactEmail: d.contact_email,
          pitchType:    d.pitch_type,
          notes:        d.notes,
        }),
      };
    case 'update_outreach': {
      const outreachId = d.outreach_id as string;
      if (!outreachId) return null;
      return {
        action: 'Update outreach',
        tool: action,
        input: details,
        endpoint: `/api/outreach/${outreachId}`,
        method: 'PATCH',
        body: stripUndefined({
          status:       d.status,
          contactName:  d.contact_name,
          contactEmail: d.contact_email,
          lastFollowUp: d.last_followup_at,
          notes:        d.notes,
        }),
      };
    }

    // ── Brands ──────────────────────────────────────────────────────────────
    case 'create_brand':
      return {
        action: 'Create brand',
        tool: action,
        input: details,
        endpoint: '/api/brands',
        method: 'POST',
        body: stripUndefined({
          brand_name:    d.brand_name,
          industry:      d.industry,
          contact_name:  d.contact_name,
          contact_email: d.contact_email,
          contact_role:  d.contact_role,
          website:       d.website,
          notes:         d.notes,
        }),
      };
    case 'update_brand': {
      const brandId = d.brand_id as string;
      if (!brandId) return null;
      return {
        action: 'Update brand',
        tool: action,
        input: details,
        endpoint: `/api/brands/${brandId}`,
        method: 'PATCH',
        body: stripUndefined({
          brand_name:    d.brand_name,
          industry:      d.industry,
          contact_name:  d.contact_name,
          contact_email: d.contact_email,
          contact_role:  d.contact_role,
          website:       d.website,
          notes:         d.notes,
        }),
      };
    }
    case 'toggle_blacklist': {
      const brandId = d.brand_id as string;
      if (!brandId) return null;
      return {
        action: d.is_blacklisted ? 'Blacklist brand' : 'Remove brand from blacklist',
        tool: action,
        input: details,
        endpoint: `/api/brands/${brandId}/blacklist`,
        method: 'POST',
        body: stripUndefined({ blacklisted: d.is_blacklisted, reason: d.reason }),
      };
    }

    // ── Invoices ────────────────────────────────────────────────────────────
    case 'create_invoice':
      return {
        action: 'Create invoice',
        tool: action,
        input: details,
        endpoint: '/api/invoices',
        method: 'POST',
        body: stripUndefined({
          brandId:   d.brand_id,
          dealId:    d.deal_id,
          amount:    d.amount,
          dueDate:   d.due_date,
          notes:     d.notes,
          lineItems: d.line_items,
        }),
      };
    case 'mark_invoice_paid': {
      const invoiceId = d.invoice_id as string;
      if (!invoiceId) return null;
      return {
        action: 'Mark invoice as paid',
        tool: action,
        input: details,
        endpoint: `/api/invoices/${invoiceId}/mark-paid`,
        method: 'POST',
        body: {},
      };
    }
    case 'send_invoice': {
      const invoiceId = d.invoice_id as string;
      if (!invoiceId) return null;
      return {
        action: 'Send invoice email',
        tool: action,
        input: details,
        endpoint: `/api/invoices/${invoiceId}/send`,
        method: 'POST',
        body: {},
      };
    }
    case 'update_invoice': {
      const invoiceId = d.invoice_id as string;
      if (!invoiceId) return null;
      return {
        action: 'Update invoice',
        tool: action,
        input: details,
        endpoint: `/api/invoices/${invoiceId}`,
        method: 'PATCH',
        body: stripUndefined({
          amount:  d.amount,
          dueDate: d.due_date,
          notes:   d.notes,
          status:  d.status,
        }),
      };
    }

    // ── Expenses ────────────────────────────────────────────────────────────
    case 'create_expense':
      return {
        action: 'Create expense',
        tool: action,
        input: details,
        endpoint: '/api/expenses',
        method: 'POST',
        body: stripUndefined({
          category:     d.category,
          description:  d.description,
          amount:       d.amount,
          date:         d.expense_date,
          isDeductible: d.is_deductible,
          dealId:       d.deal_id,
        }),
      };
    case 'update_expense': {
      const expenseId = d.expense_id as string;
      if (!expenseId) return null;
      return {
        action: 'Update expense',
        tool: action,
        input: details,
        endpoint: `/api/expenses/${expenseId}`,
        method: 'PATCH',
        body: stripUndefined({
          category:     d.category,
          description:  d.description,
          amount:       d.amount,
          date:         d.expense_date,
          isDeductible: d.is_deductible,
        }),
      };
    }
    case 'delete_expense': {
      const expenseId = d.expense_id as string;
      if (!expenseId) return null;
      return {
        action: 'Delete expense',
        tool: action,
        input: details,
        endpoint: `/api/expenses/${expenseId}`,
        method: 'DELETE',
        body: {},
      };
    }

    // ── Notifications ───────────────────────────────────────────────────────
    case 'mark_notification_read': {
      const notifId = d.notification_id as string;
      if (!notifId) return null;
      return {
        action: 'Mark notification as read',
        tool: action,
        input: details,
        endpoint: `/api/notifications/${notifId}/read`,
        method: 'PATCH',
        body: {},
      };
    }
    case 'mark_all_notifications_read':
      return {
        action: 'Mark all notifications as read',
        tool: action,
        input: details,
        endpoint: '/api/notifications/read-all',
        method: 'POST',
        body: {},
      };

    // ── Social Stats ────────────────────────────────────────────────────────
    case 'update_social_stats': {
      const platform = d.platform as string;
      if (!platform) return null;
      // Named platforms only. 'other'/custom platforms are addressed by row id
      // (PUT /other/:id), not platform name — AI writes for those aren't wired
      // up yet, so don't route them to the named endpoint (it would 400).
      if (platform === 'other') return null;
      return {
        action: 'Update social stats',
        tool: action,
        input: details,
        endpoint: `/api/social-stats/${platform}`,
        method: 'PUT',
        body: stripUndefined({
          followers:      d.followers,
          engagementRate: d.engagement_rate,
          monthlyReach:   d.monthly_reach,
          avgViews:       d.avg_views,
          username:       d.username,
        }),
      };
    }

    // ── Media Kit ──────────────────────────────────────────────────────────
    case 'update_media_kit':
      return {
        action: 'Update media kit',
        tool: action,
        input: details,
        endpoint: '/api/media-kit',
        method: 'PUT',
        body: stripUndefined({
          template:       d.template,
          customHeadline: d.custom_headline,
          customBio:      d.custom_bio,
          featuredDeals:  d.highlight_deals,
        }),
      };

    // ── Goals ──────────────────────────────────────────────────────────────
    case 'update_goals':
      return {
        action: 'Update goals',
        tool: action,
        input: details,
        endpoint: '/api/goals',
        method: 'PUT',
        body: stripUndefined({
          annual_income_goal:     d.annual_income_goal,
          ideal_deal_size_min:    d.ideal_deal_size_min,
          ideal_deal_size_max:    d.ideal_deal_size_max,
          weekly_hours_available: d.weekly_hours_available,
          dream_brands:           d.dream_brands,
          content_focus:          Array.isArray(d.content_focus)
                                    ? d.content_focus
                                    : (d.content_focus ? [d.content_focus] : undefined),
          platforms_focus:        d.primary_platforms,
          preferred_deal_types:   d.preferred_deal_types,
          stop_doing:             d.stop_doing,
          goal_notes:             d.notes,
        }),
      };

    // ── NDAs ───────────────────────────────────────────────────────────────
    case 'create_nda':
      return {
        action: 'Log NDA',
        tool: action,
        input: details,
        endpoint: '/api/ndas',
        method: 'POST',
        body: stripUndefined({
          brand_name:   d.brand_name,
          brand_id:     d.brand_id,
          description:  d.description,
          start_date:   d.start_date,
          expiry_date:  d.expiry_date,
          restrictions: d.restrictions,
          file_url:     d.file_url,
          status:       d.status,
        }),
      };
    case 'update_nda': {
      const ndaId = d.nda_id as string;
      if (!ndaId) return null;
      return {
        action: 'Update NDA',
        tool: action,
        input: details,
        endpoint: `/api/ndas/${ndaId}`,
        method: 'PATCH',
        body: stripUndefined({
          brand_name:   d.brand_name,
          brand_id:     d.brand_id,
          description:  d.description,
          start_date:   d.start_date,
          expiry_date:  d.expiry_date,
          restrictions: d.restrictions,
          file_url:     d.file_url,
          status:       d.status,
        }),
      };
    }

    // ── Unknown action ──────────────────────────────────────────────────────
    // Returning null here means the chat hook won't render a card and will
    // log a warning. Add a case above to wire up the action.
    default:
      return null;
  }
}
